/**
 * Dependency-free 3D preview of a solar layout.
 *
 * World space: metres, x = east, y = north, z = up, origin at the centroid of
 * all roofs. `buildScene()` turns the designer's state (lat/lng polygons +
 * panel footprints) into groups of coloured faces; `renderScene()` projects
 * them with a perspective camera and paints them back-to-front.
 *
 * Painter's algorithm is enough here because every object sits on a
 * "support" (ground or one roof) and supports never overlap in plan: groups
 * are drawn far→near, and inside a group the support is drawn first, then
 * everything standing on it sorted by depth.
 */
import { latlngToXY, polygonCentroid, pointInPolygonXY, shrinkPolygon, panelKey, roofSurface, sunOffsetAt, computeRowShading, clipConvexPolygons } from "@/utils/panelCalc";

const DEG = Math.PI / 180;

export const COLORS = {
  ground:      [214, 226, 214],
  grid:        [140, 162, 142],
  roofTop:     [236, 222, 196],
  roofSide:    [196, 176, 142],
  slabTop:     [222, 224, 214],
  parapet:     [226, 214, 190],
  obstacleTop: [232, 120, 108],
  obstacleSide:[196, 80, 72],
  keepout:     [150, 156, 164],
  panel:       [28, 66, 156],
  panelBack:   [112, 122, 140],
  rack:        [110, 116, 124],
  shadow:      [30, 41, 59],
};

const SLAB_THICKNESS = 0.15;   // ground-level "roof" rendered as a thin slab
const PARAPET_THICKNESS = 0.23;
const STAND_H = { flat: 0.3, pitched: 0.08 };

// ── Geometry helpers ───────────────────────────────────────────────────────
function normal(pts) {
  // Newell's method — robust for concave / slightly non-planar polygons
  let nx = 0, ny = 0, nz = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    nx += (a.y - b.y) * (a.z + b.z);
    ny += (a.z - b.z) * (a.x + b.x);
    nz += (a.x - b.x) * (a.y + b.y);
  }
  const l = Math.hypot(nx, ny, nz) || 1;
  return { x: nx / l, y: ny / l, z: nz / l };
}

function isCCW(poly) {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    s += (b.x - a.x) * (b.y + a.y);
  }
  return s < 0;
}

function hullXY(points) {
  // Andrew's monotone chain
  const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  if (pts.length < 3) return pts;
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower = [];
  for (const p of pts) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop(); lower.push(p); }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) { const p = pts[i]; while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop(); upper.push(p); }
  upper.pop(); lower.pop();
  return lower.concat(upper);
}

/** Faces of a vertical prism from z0 to z1 over an XY polygon. Outward normals. */
function prismFaces(polyXY, z0, z1, topColor, sideColor, kind) {
  const poly = isCCW(polyXY) ? polyXY : [...polyXY].reverse();
  const faces = [];
  faces.push({ pts: poly.map(p => ({ x: p.x, y: p.y, z: z1 })), color: topColor, kind, cull: true, outline: true });
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    // CCW top polygon → outward side normal when winding a(z0)→b(z0)→b(z1)→a(z1)
    faces.push({ pts: [{ x: a.x, y: a.y, z: z0 }, { x: b.x, y: b.y, z: z0 }, { x: b.x, y: b.y, z: z1 }, { x: a.x, y: a.y, z: z1 }], color: sideColor, kind, cull: true, outline: true });
  }
  return faces;
}

// ── Scene construction ─────────────────────────────────────────────────────
/**
 * @param roofPolygons   [[{lat,lng}]]
 * @param roofSpecs      per-roof spec ({roofHeightM, parapetHeightM, tiltDeg, orientation, panelLengthM, panelWidthM, modulesPerGroup, colGapM, mountType, azimuthDeg})
 * @param obstaclePolygons [[{lat,lng}]]
 * @param obstacleHeights  [m]; <= 0 → keep-out
 * @param panels         layout.panels ({corners[4], roofIdx, row, col, tableSlot})
 * @param removedKeys    Set of "roofIdx:row:col:slot" the user removed
 * @param spec           fallback spec
 */
export function buildScene({ roofPolygons = [], roofSpecs = [], obstaclePolygons = [], obstacleHeights = [], panels = [], removedKeys = new Set(), spec = {}, sunHourAngle = null, latitude = null }) {
  const allPts = roofPolygons.flat().concat(obstaclePolygons.flat());
  if (!allPts.length) return null;
  const origin = polygonCentroid(allPts);
  const toXY = (p) => latlngToXY(p.lat, p.lng, origin.lat, origin.lng);
  const lat = latitude != null ? latitude : origin.lat;
  const sun = sunHourAngle != null ? sunOffsetAt(lat, sunHourAngle) : null;

  const roofsXY = roofPolygons.map(r => r.map(toXY));
  const obsXY = obstaclePolygons.map(o => o.map(toXY));

  // Bounds for ground + camera framing
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = 0;
  const grow = (p) => { if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x; if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y; };
  roofsXY.flat().forEach(grow); obsXY.flat().forEach(grow);

  const groups = [];   // { support: faces[], items: faces[], anchor: {x,y,z}, label }
  const labels = [];   // { x, y, z, text, color }

  // Roof groups
  const roofTopZ = roofsXY.map((_, i) => {
    const h = Number((roofSpecs[i] || spec).roofHeightM) || 0;
    return h > 0.05 ? h : SLAB_THICKNESS;
  });
  // Roof surface height (eave level + slope) — flat roofs return a constant
  const surfaces = roofsXY.map((poly, i) => {
    const rs = roofSpecs[i] || spec;
    const sf = roofSurface(poly, { roofShape: rs.mountType === "pitched" ? (rs.roofShape || "flat") : "flat", roofPitchDeg: Number(rs.roofPitchDeg) || 0, azimuthDeg: Number(rs.azimuthDeg) || 180 });
    return { ...sf, zAt: (pt) => roofTopZ[i] + sf.z(pt) };
  });
  const roofZ = (i, pt) => surfaces[i] ? surfaces[i].zAt(pt) : roofTopZ[i];
  roofsXY.forEach((poly, i) => {
    const rs = roofSpecs[i] || spec;
    const top = roofTopZ[i];
    const isSlab = top === SLAB_THICKNESS;
    const sf = surfaces[i];
    let support;
    if (sf.shape === "flat") {
      support = prismFaces(poly, 0, top, isSlab ? COLORS.slabTop : COLORS.roofTop, COLORS.roofSide, "roof");
    } else {
      // sloped: top polygon(s) follow the surface, walls rise from the ground to the eave/slope line
      const ccw = isCCW(poly) ? poly : [...poly].reverse();
      support = [];
      const pieces = sf.shape === "gable" ? sf.halves.map(h => h.poly) : [ccw];
      pieces.forEach(pc => {
        const pw = isCCW(pc) ? pc : [...pc].reverse();
        support.push({ pts: pw.map(pt => ({ x: pt.x, y: pt.y, z: sf.zAt(pt) })), color: COLORS.roofTop, kind: "roof", cull: true, outline: true });
      });
      for (let k = 0; k < ccw.length; k++) {
        const a = ccw[k], b = ccw[(k + 1) % ccw.length];
        // wall may need a kink where it crosses the ridge; split at the ridge for gables
        const wallPts = [{ x: a.x, y: a.y, z: 0 }, { x: b.x, y: b.y, z: 0 }];
        if (sf.shape === "gable" && sf.ridge) {
          const [r0, r1] = sf.ridge;
          const d = { x: r1.x - r0.x, y: r1.y - r0.y };
          const side = (pt) => (pt.x - r0.x) * d.y - (pt.y - r0.y) * d.x;
          const sa = side(a), sb = side(b);
          if ((sa > 0) !== (sb > 0) && Math.abs(sa - sb) > 1e-9) {
            const t = sa / (sa - sb);
            const m = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
            wallPts.push({ x: b.x, y: b.y, z: sf.zAt(b) }, { x: m.x, y: m.y, z: sf.zAt(m) }, { x: a.x, y: a.y, z: sf.zAt(a) });
          } else {
            wallPts.push({ x: b.x, y: b.y, z: sf.zAt(b) }, { x: a.x, y: a.y, z: sf.zAt(a) });
          }
        } else {
          wallPts.push({ x: b.x, y: b.y, z: sf.zAt(b) }, { x: a.x, y: a.y, z: sf.zAt(a) });
        }
        support.push({ pts: wallPts, color: COLORS.roofSide, kind: "roof", cull: true, outline: true });
      }
      if (sf.ridge) {
        // ridge line clipped to the roof footprint for the drawing
        const inside = sf.halves[0] && sf.halves[1] ? sf.halves[0].poly.filter(pt => sf.halves[1].poly.some(q => Math.hypot(q.x - pt.x, q.y - pt.y) < 1e-6)) : [];
        if (inside.length >= 2) {
          const [ra, rb] = [inside[0], inside[inside.length - 1]];
          support.push({ pts: [{ x: ra.x, y: ra.y, z: sf.zAt(ra) + 0.03 }, { x: rb.x, y: rb.y, z: sf.zAt(rb) + 0.03 }], color: [120, 96, 60], kind: "line" });
        }
      }
    }
    const items = [];
    const par = Number(rs.parapetHeightM) || 0;
    if (par > 0.02 && sf.shape === "flat") {
      const inner = shrinkPolygon(poly, PARAPET_THICKNESS);
      if (inner.length >= 3) {
        // outer wall (outward normals), inner wall (inward normals), cap ring
        prismFaces(poly, top, top + par, COLORS.parapet, COLORS.parapet, "parapet").slice(1).forEach(f => items.push(f));
        prismFaces(inner, top, top + par, COLORS.parapet, COLORS.parapet, "parapet").slice(1)
          .forEach(f => items.push({ ...f, pts: [...f.pts].reverse() }));
        const o = isCCW(poly) ? poly : [...poly].reverse();
        const inn = isCCW(inner) ? inner : [...inner].reverse();
        if (o.length === inn.length) {
          for (let k = 0; k < o.length; k++) {
            const a = o[k], b = o[(k + 1) % o.length], c = inn[(k + 1) % inn.length], d = inn[k];
            items.push({ pts: [{ x: a.x, y: a.y, z: top + par }, { x: b.x, y: b.y, z: top + par }, { x: c.x, y: c.y, z: top + par }, { x: d.x, y: d.y, z: top + par }], color: COLORS.parapet, kind: "parapet", cull: true, outline: true });
          }
        }
      }
      maxZ = Math.max(maxZ, top + par);
    }
    maxZ = Math.max(maxZ, top, ...poly.map(pt => sf.zAt(pt)));
    const c = poly.reduce((s, p) => ({ x: s.x + p.x / poly.length, y: s.y + p.y / poly.length }), { x: 0, y: 0 });
    groups.push({ support, items, anchor: { x: c.x, y: c.y, z: top }, roofIdx: i });
    const pitchTxt = sf.shape !== "flat" ? ` · ${rs.roofPitchDeg}° ${sf.shape === "gable" ? "gable" : "slope"}` : "";
    if (roofsXY.length > 1 || !isSlab || pitchTxt) labels.push({ x: c.x, y: c.y, z: sf.zAt(c) + par + 0.05, text: `Roof ${i + 1}${!isSlab ? ` · ${top} m` : ""}${pitchTxt}`, color: "#7C5A1E", kind: "roof" });
  });

  // Ground group (drawn first)
  const padX = Math.max(6, (maxX - minX) * 0.35), padY = Math.max(6, (maxY - minY) * 0.35);
  const g0 = { x: minX - padX, y: minY - padY }, g1 = { x: maxX + padX, y: maxY + padY };
  const ground = { pts: [{ x: g0.x, y: g0.y, z: 0 }, { x: g1.x, y: g0.y, z: 0 }, { x: g1.x, y: g1.y, z: 0 }, { x: g0.x, y: g1.y, z: 0 }], color: COLORS.ground, kind: "ground", cull: false, outline: false };
  const groundGroup = { support: [ground], items: [], anchor: { x: (g0.x + g1.x) / 2, y: (g0.y + g1.y) / 2, z: -1e3 }, ground: true, extent: { g0, g1 } };

  // Obstacles → attach to the roof that contains their centroid, else ground
  obsXY.forEach((poly, i) => {
    const h = obstacleHeights[i] != null ? Number(obstacleHeights[i]) : 1;
    const c = poly.reduce((s, p) => ({ x: s.x + p.x / poly.length, y: s.y + p.y / poly.length }), { x: 0, y: 0 });
    let host = groundGroup, base = 0;
    for (let r = 0; r < roofsXY.length; r++) {
      if (pointInPolygonXY(c, roofsXY[r])) { host = groups[r]; base = roofZ(r, c); break; }
    }
    if (h <= 0) {
      host.items.push({ pts: poly.map(p => ({ x: p.x, y: p.y, z: base + 0.02 })), color: COLORS.keepout, kind: "keepout", cull: false, outline: true, alpha: 0.75, hatch: true });
      labels.push({ x: c.x, y: c.y, z: base + 0.05, text: "keep-out", color: "#4B5563", kind: "obstacle" });
    } else {
      const faces = prismFaces(poly, base, base + h, COLORS.obstacleTop, COLORS.obstacleSide, "obstacle");
      if (sun) {
        // shadow on the supporting surface: hull of footprint ∪ footprint shifted by the height
        const shifted = poly.map(pt => ({ x: pt.x + sun.dx * h, y: pt.y + sun.dy * h }));
        const hull = hullXY([...poly, ...shifted]);
        if (hull.length >= 3) host.items.push({ pts: hull.map(pt => ({ x: pt.x, y: pt.y, z: base + 0.01 })), color: COLORS.shadow, kind: "shadow", cull: false, outline: false, alpha: 0.45 });
      }
      if (host === groundGroup) {
        // A free-standing building/tree is its own group so it sorts far→near
        // against the roofs instead of always being painted first with the ground.
        groups.push({ support: [], items: faces, anchor: { x: c.x, y: c.y, z: h / 2 }, obstacle: true });
      } else {
        faces.forEach(f => host.items.push(f));
      }
      labels.push({ x: c.x, y: c.y, z: base + h + 0.05, text: `${h} m`, color: "#991B1B", kind: "obstacle" });
      maxZ = Math.max(maxZ, base + h);
    }
  });

  // Panels
  let panelCount = 0;
  const panels3d = [];          // for row-shading
  const panelFaces = new Map(); // key → face (to overlay shadows)
  panels.forEach(p => {
    const key = panelKey(p);
    if (removedKeys.has(key)) return;
    const g = groups[p.roofIdx]; if (!g) return;
    const rs = roofSpecs[p.roofIdx] || spec;
    const flush = rs.mountType === "pitched" && (Number(rs.roofPitchDeg) || 0) > 0;
    // flush on a sloped roof: the surface already carries the tilt; on a flat roof the rack does
    const tilt = flush ? 0 : ((Number(p.tiltDeg ?? rs.tiltDeg) || 0) * DEG);
    const faceDim = rs.orientation === "landscape" ? Number(rs.panelWidthM) : Number(rs.panelLengthM);
    const rise = (faceDim || 2) * Math.sin(tilt);
    const mpg = Math.max(1, Math.round(Number(rs.modulesPerGroup) || 1));
    const gapRise = (Number(rs.colGapM) || 0) * Math.sin(tilt);
    const stand = STAND_H[rs.mountType === "pitched" ? "pitched" : "flat"];
    const slot = p.tableSlot || 0;
    const xy = p.corners.map(toXY);
    // corners 0,3 are on the +face (sun-facing, low) edge; 1,2 on the back (high) edge
    const rackFront = stand + (mpg - 1 - slot) * (rise + gapRise);
    const rackBack = rackFront + rise;
    const pts = [
      { x: xy[0].x, y: xy[0].y, z: roofZ(p.roofIdx, xy[0]) + rackFront }, { x: xy[1].x, y: xy[1].y, z: roofZ(p.roofIdx, xy[1]) + rackBack },
      { x: xy[2].x, y: xy[2].y, z: roofZ(p.roofIdx, xy[2]) + rackBack }, { x: xy[3].x, y: xy[3].y, z: roofZ(p.roofIdx, xy[3]) + rackFront },
    ];
    // wind so the normal points up (Newell on the footprint order may be either way)
    const n = normal(pts);
    const face = { pts: n.z < 0 ? pts.slice().reverse() : pts, color: COLORS.panel, backColor: COLORS.panelBack, kind: "panel", cull: false, outline: true, twoSided: true, key };
    g.items.push(face);
    panelFaces.set(key, { face, plane: pts });
    // heights above the local roof surface at the low edge, for shading (on a flush roof the
    // surface slope is what raises the back edge)
    panels3d.push({ key, roofIdx: p.roofIdx, footprint: xy, zFront: rackFront, zBack: rackBack + (flush ? Math.max(0, roofZ(p.roofIdx, xy[1]) - roofZ(p.roofIdx, xy[0])) : 0) });
    // rear support leg on the last (highest) module of each table
    if (slot === 0 && rackBack > 0.35) {
      const mid = { x: (xy[1].x + xy[2].x) / 2, y: (xy[1].y + xy[2].y) / 2 };
      g.items.push({ pts: [{ x: mid.x, y: mid.y, z: roofZ(p.roofIdx, mid) }, { x: mid.x, y: mid.y, z: roofZ(p.roofIdx, mid) + rackBack }], color: COLORS.rack, kind: "line" });
    }
    panelCount++;
    maxZ = Math.max(maxZ, pts[1].z, pts[2].z);
  });

  // Inter-row shading at the chosen sun position: dark patches on the roof and on the shaded panels
  let shading = null;
  if (sun && panels3d.length) {
    shading = computeRowShading(panels3d, sun);
    shading.ground.forEach(sh => {
      const g = groups[panels3d.find(x => x.key === sh.key)?.roofIdx];
      if (!g) return;
      const ri = panels3d.find(x => x.key === sh.key).roofIdx;
      g.items.push({ pts: sh.poly.map(pt => ({ x: pt.x, y: pt.y, z: roofZ(ri, pt) + 0.012 })), color: COLORS.shadow, kind: "shadow", cull: false, outline: false, alpha: 0.35 });
    });
    shading.onPanels.forEach(sh => {
      const target = panelFaces.get(sh.key); if (!target) return;
      // lift the shaded patch onto the panel's plane
      const [p0, p1, p2] = target.plane;
      const u = { x: p1.x - p0.x, y: p1.y - p0.y, z: p1.z - p0.z }, v = { x: p2.x - p1.x, y: p2.y - p1.y, z: p2.z - p1.z };
      const nn = { x: u.y * v.z - u.z * v.y, y: u.z * v.x - u.x * v.z, z: u.x * v.y - u.y * v.x };
      if (Math.abs(nn.z) < 1e-9) return;
      const zOn = (pt) => p0.z - ((pt.x - p0.x) * nn.x + (pt.y - p0.y) * nn.y) / nn.z;
      const ri = panels3d.find(x => x.key === sh.key).roofIdx;
      groups[ri].items.push({ pts: sh.poly.map(pt => ({ x: pt.x, y: pt.y, z: zOn(pt) + 0.015 })), color: COLORS.shadow, kind: "shadow", cull: false, outline: false, alpha: 0.55, after: sh.key });
    });
  }

  const center = { x: (minX + maxX) / 2, y: (minY + maxY) / 2, z: maxZ / 2 };
  const radius = Math.max(4, Math.hypot(maxX - minX, maxY - minY) / 2, maxZ);
  const gridStep = radius > 60 ? 10 : radius > 25 ? 5 : 2;
  return {
    groups: [groundGroup, ...groups], labels, center, radius, gridStep, ground: groundGroup.extent,
    azimuthDeg: Number((roofSpecs[0] || spec).azimuthDeg) || 180, panelCount, origin,
    sun: sun ? { ...sun, hourAngle: sunHourAngle } : null,
    shading: shading ? { shadedPanels: shading.shadedKeys.size, worst: Math.max(0, ...shading.onPanels.map(o => o.fraction)) } : null,
  };
}

// ── Camera ─────────────────────────────────────────────────────────────────
export function defaultCamera(scene, preset = "iso") {
  const p = { iso: { yaw: 35 * DEG, pitch: 38 * DEG }, top: { yaw: 0, pitch: 89 * DEG }, south: { yaw: 0, pitch: 22 * DEG }, north: { yaw: Math.PI, pitch: 22 * DEG } }[preset] || { yaw: 35 * DEG, pitch: 38 * DEG };
  return { target: { ...scene.center }, dist: scene.radius * (preset === "top" ? 2.2 : 2.4), yaw: p.yaw, pitch: p.pitch, fov: 45 * DEG };
}

function cameraBasis(cam) {
  const cp = Math.cos(cam.pitch);
  const pos = { x: cam.target.x + cam.dist * Math.sin(cam.yaw) * cp, y: cam.target.y - cam.dist * Math.cos(cam.yaw) * cp, z: cam.target.z + cam.dist * Math.sin(cam.pitch) };
  let f = { x: cam.target.x - pos.x, y: cam.target.y - pos.y, z: cam.target.z - pos.z };
  const fl = Math.hypot(f.x, f.y, f.z) || 1; f = { x: f.x / fl, y: f.y / fl, z: f.z / fl };
  // right = f × up(0,0,1)
  let r = { x: f.y, y: -f.x, z: 0 }; const rl = Math.hypot(r.x, r.y) || 1; r = { x: r.x / rl, y: r.y / rl, z: 0 };
  // up = r × f
  const u = { x: r.y * f.z - r.z * f.y, y: r.z * f.x - r.x * f.z, z: r.x * f.y - r.y * f.x };
  return { pos, f, r, u };
}

/** Sutherland–Hodgman clip of a view-space polygon against z = near. */
function clipNear(vpts, near) {
  if (vpts.every(p => p.z >= near)) return vpts;
  const out = [];
  for (let i = 0; i < vpts.length; i++) {
    const a = vpts[i], b = vpts[(i + 1) % vpts.length];
    const ain = a.z >= near, bin = b.z >= near;
    if (ain) out.push(a);
    if (ain !== bin) {
      const t = (near - a.z) / (b.z - a.z);
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: near });
    }
  }
  return out;
}

// ── Rendering ──────────────────────────────────────────────────────────────
const LIGHT_EL = 52 * DEG;
function lightDir(azimuthDeg) {
  const az = azimuthDeg * DEG;
  return { x: Math.sin(az) * Math.cos(LIGHT_EL), y: Math.cos(az) * Math.cos(LIGHT_EL), z: Math.sin(LIGHT_EL) };
}

function rgb(c, k, a = 1) {
  const f = (v) => Math.max(0, Math.min(255, Math.round(v * k)));
  return a >= 1 ? `rgb(${f(c[0])},${f(c[1])},${f(c[2])})` : `rgba(${f(c[0])},${f(c[1])},${f(c[2])},${a})`;
}

/**
 * Draw the scene. `ctx` is a 2D context already scaled for devicePixelRatio;
 * `w`/`h` are CSS pixels. Returns the projector so callers can hit-test/label.
 */
export function renderScene(ctx, scene, cam, w, h, opts = {}) {
  const { pos, f, r, u } = cameraBasis(cam);
  const focal = (h / 2) / Math.tan(cam.fov / 2);
  const near = 0.2;
  const L = lightDir(scene.azimuthDeg);
  const showLabels = opts.labels !== false;

  const toView = (p) => { const vx = p.x - pos.x, vy = p.y - pos.y, vz = p.z - pos.z; return { x: vx * r.x + vy * r.y + vz * r.z, y: vx * u.x + vy * u.y + vz * u.z, z: vx * f.x + vy * f.y + vz * f.z }; };
  const toScreen = (v) => ({ x: w / 2 + (v.x * focal) / v.z, y: h / 2 - (v.y * focal) / v.z });
  const project = (p) => { const v = toView(p); return v.z < near ? null : toScreen(v); };

  // background
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "#EAF2FB"); grad.addColorStop(1, "#F7FAF7");
  ctx.fillStyle = grad; ctx.fillRect(0, 0, w, h);

  const drawFace = (face) => {
    if (face.kind === "line") {
      const a = project(face.pts[0]), b = project(face.pts[1]);
      if (!a || !b) return;
      ctx.strokeStyle = rgb(face.color, 1); ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      return;
    }
    const n = normal(face.pts);
    const toCam = { x: pos.x - face.pts[0].x, y: pos.y - face.pts[0].y, z: pos.z - face.pts[0].z };
    const facing = n.x * toCam.x + n.y * toCam.y + n.z * toCam.z;
    if (face.cull && facing <= 0) return;
    let vpts = clipNear(face.pts.map(toView), near);
    if (vpts.length < 3) return;
    const spts = vpts.map(toScreen);
    let lambert = n.x * L.x + n.y * L.y + n.z * L.z;
    let color = face.color;
    if (face.twoSided && facing < 0) { color = face.backColor || face.color; lambert = -lambert; }
    const k = 0.58 + 0.42 * Math.max(0, lambert);
    ctx.beginPath();
    spts.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y));
    ctx.closePath();
    ctx.fillStyle = rgb(color, k, face.alpha ?? 1);
    ctx.fill();
    if (face.hatch) {
      ctx.save(); ctx.clip();
      ctx.strokeStyle = "rgba(55,65,81,0.45)"; ctx.lineWidth = 1;
      const xs = spts.map(p => p.x), ys = spts.map(p => p.y);
      const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
      for (let d = x0 - (y1 - y0); d < x1; d += 8) { ctx.beginPath(); ctx.moveTo(d, y1); ctx.lineTo(d + (y1 - y0), y0); ctx.stroke(); }
      ctx.restore();
    }
    if (face.outline) {
      ctx.strokeStyle = face.kind === "panel" ? "rgba(255,255,255,0.55)" : "rgba(40,40,40,0.35)";
      ctx.lineWidth = face.kind === "panel" ? 0.6 : 0.8;
      ctx.stroke();
    }
  };

  const depthOf = (face) => { let s = 0; for (const p of face.pts) { s += (p.x - pos.x) * f.x + (p.y - pos.y) * f.y + (p.z - pos.z) * f.z; } return s / face.pts.length - (face.kind === "shadow" ? 0.02 : 0); };
  const byDepth = (faces) => faces.map(fc => ({ fc, d: depthOf(fc) })).sort((a, b) => b.d - a.d).map(o => o.fc);

  // ground grid (1 m minor every 5 m major) — drawn right after the ground quad
  const drawGrid = () => {
    const { g0, g1 } = scene.ground;
    const step = scene.gridStep || 5;
    ctx.lineWidth = 0.6;
    for (let x = Math.ceil(g0.x / step) * step; x <= g1.x; x += step) {
      const a = project({ x, y: g0.y, z: 0.005 }), b = project({ x, y: g1.y, z: 0.005 });
      if (!a || !b) continue;
      ctx.strokeStyle = rgb(COLORS.grid, 1, x % (step * 5) === 0 ? 0.7 : 0.35);
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    }
    for (let y = Math.ceil(g0.y / step) * step; y <= g1.y; y += step) {
      const a = project({ x: g0.x, y, z: 0.005 }), b = project({ x: g1.x, y, z: 0.005 });
      if (!a || !b) continue;
      ctx.strokeStyle = rgb(COLORS.grid, 1, y % (step * 5) === 0 ? 0.7 : 0.35);
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    }
  };

  // sun direction arrow on the ground from the scene centre toward the azimuth
  // (drawn with the ground so roofs and panels correctly sit on top of it)
  const drawSun = () => {
    const az = (scene.sun ? scene.sun.azimuthDeg : scene.azimuthDeg) * DEG;
    const len = Math.max(3, scene.radius * 0.6);
    const a = project({ x: scene.center.x, y: scene.center.y, z: 0.05 });
    const b = project({ x: scene.center.x + Math.sin(az) * len, y: scene.center.y + Math.cos(az) * len, z: 0.05 });
    if (a && b) {
      ctx.strokeStyle = "rgba(217,119,6,0.85)"; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = "#F59E0B"; ctx.beginPath(); ctx.arc(b.x, b.y, 7, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#7C2D12"; ctx.font = "600 11px Manrope, sans-serif"; ctx.textAlign = "center";
      ctx.fillText(scene.sun ? `winter sun ${scene.sun.hourAngle < 0 ? "9 am" : scene.sun.hourAngle > 0 ? "3 pm" : "noon"} · alt ${Math.round(scene.sun.altitudeDeg)}°` : `sun · ${scene.azimuthDeg}°`, b.x, b.y + 20);
    }
  };

  // groups far → near by anchor depth (ground always first)
  const ordered = scene.groups.map(g => ({ g, d: g.ground ? Infinity : depthOf({ pts: [g.anchor] }) })).sort((a, b) => b.d - a.d).map(o => o.g);
  ordered.forEach(g => {
    byDepth(g.support).forEach(drawFace);
    if (g.ground) { drawGrid(); drawSun(); }
    byDepth(g.items).forEach(drawFace);
  });

  // labels
  if (showLabels) {
    ctx.font = "600 11px Manrope, sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "bottom";
    scene.labels.forEach(l => {
      const s = project(l); if (!s) return;
      const tw = ctx.measureText(l.text).width + 10;
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.fillRect(s.x - tw / 2, s.y - 16, tw, 15);
      ctx.fillStyle = l.color; ctx.fillText(l.text, s.x, s.y - 3);
    });
  }

  // compass (screen-space, bottom-left) — north projected through the camera yaw
  {
    const cx = 34, cy = h - 34, R = 20;
    ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.beginPath(); ctx.arc(cx, cy, R + 6, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "rgba(46,100,68,0.25)"; ctx.lineWidth = 1; ctx.stroke();
    // world north (0,1,0) in view-right/up plane
    const nv = { x: r.y, y: u.y };
    const nl = Math.hypot(nv.x, nv.y) || 1;
    const nx = nv.x / nl, ny = -nv.y / nl;
    ctx.strokeStyle = "#DC2626"; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.moveTo(cx - nx * R * 0.5, cy - ny * R * 0.5); ctx.lineTo(cx + nx * R, cy + ny * R); ctx.stroke();
    ctx.fillStyle = "#DC2626"; ctx.font = "700 10px Manrope, sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("N", cx + nx * (R + 11), cy + ny * (R + 11));
  }

  return { project, pos };
}

/** Render a scene to a PNG data-URL (used by the PDF export). */
export function renderToDataURL(scene, w, h, preset = "iso") {
  if (!scene) return null;
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  renderScene(ctx, scene, defaultCamera(scene, preset), w, h, { labels: true });
  return canvas.toDataURL("image/jpeg", 0.9);
}
